import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn } from '~/utils/cn';

interface AssistantMarkdownProps {
  /** Markdown source from the agent (CommonMark + GFM). Rendered live as tokens stream in. */
  text: string;
}

/**
 * Renders an assistant Q&A answer as Markdown for the compact Lily Agent
 * panel. Tailwind's preflight strips list markers / heading sizes / code
 * styling and the `@tailwindcss/typography` (`prose`) plugin isn't installed,
 * so every element react-markdown emits is styled explicitly via child
 * selectors — tuned for the narrow panel (xs body, design tokens only). GFM is
 * enabled for tables, strikethrough, task lists, and autolinks.
 */
export function AssistantMarkdown({ text }: AssistantMarkdownProps) {
  return (
    <div
      className={cn(
        'text-foreground space-y-2 text-xs leading-5 break-words',
        '[&_strong]:text-foreground [&_strong]:font-semibold',
        '[&_em]:italic',
        '[&_del]:line-through',
        '[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2',
        '[&_ul]:list-disc [&_ul]:space-y-0.5 [&_ul]:pl-4',
        '[&_ol]:list-decimal [&_ol]:space-y-0.5 [&_ol]:pl-4',
        '[&_li]:marker:text-muted-foreground',
        '[&_h1]:text-foreground [&_h1]:text-sm [&_h1]:font-semibold',
        '[&_h2]:text-foreground [&_h2]:text-sm [&_h2]:font-semibold',
        '[&_h3]:text-foreground [&_h3]:font-semibold',
        '[&_code]:bg-muted [&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em]',
        '[&_pre]:bg-muted [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:p-2',
        '[&_pre>code]:bg-transparent [&_pre>code]:p-0',
        '[&_blockquote]:border-border [&_blockquote]:text-muted-foreground [&_blockquote]:border-l-2 [&_blockquote]:pl-2',
        '[&_hr]:border-border [&_hr]:my-2',
        // block + overflow-x lets a wide data table scroll horizontally inside the
        // narrow panel rather than overflowing it (Lily often answers with tables);
        // w-full keeps tables that already fit looking full-width as before.
        '[&_table]:block [&_table]:w-full [&_table]:overflow-x-auto [&_table]:text-left',
        '[&_th]:border-border [&_td]:border-border [&_td]:border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:px-2 [&_th]:py-1',
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    </div>
  );
}
